import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Buildings, ArrowRight, CheckCircle, WarningCircle, DownloadSimple, MapPin, ArrowLeft,
  SealCheck, ChatText, HourglassMedium,
} from "@phosphor-icons/react";
import { API, formatApiErrorDetail } from "@/lib/api";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const HERO = "https://images.pexels.com/photos/10267196/pexels-photo-10267196.jpeg";

export default function PublicPortal() {
  const [params] = useSearchParams();
  const [code, setCode] = useState("");
  const [dossier, setDossier] = useState(null);
  const [loading, setLoading] = useState(false);
  const [remarks, setRemarks] = useState("");
  const [showRemarks, setShowRemarks] = useState(false);
  const [message, setMessage] = useState("");
  const [showMessage, setShowMessage] = useState(false);
  const [acting, setActing] = useState(false);
  const [confirmValidate, setConfirmValidate] = useState(false);

  const fetchDossier = async (accessCode) => {
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/public/access`, { access_code: accessCode });
      setDossier(data);
      const action = params.get("action");
      if (data.report_status === "provisional" && data.payment_status === "paid") {
        if (action === "remarks") setShowRemarks(true);
        if (action === "validate") setConfirmValidate(true);
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Code invalide");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const c = params.get("code");
    if (c) { setCode(c); fetchDossier(c); }
    // eslint-disable-next-line
  }, []);

  const submit = (e) => { e.preventDefault(); if (code.trim()) fetchDossier(code.trim()); };

  const download = async () => {
    try {
      const res = await axios.get(`${API}/public/download/${code.trim().toUpperCase()}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = dossier.current_version?.filename || "rapport.pdf";
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error("Téléchargement impossible");
    }
  };

  const downloadDoc = async (doc) => {
    try {
      const res = await axios.get(`${API}/public/documents/${code.trim().toUpperCase()}/${doc.id}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = doc.filename || "rapport.pdf";
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error("Téléchargement impossible");
    }
  };

  const validate = async () => {    setActing(true);
    try {
      const { data } = await axios.post(`${API}/public/validate`, { access_code: code.trim().toUpperCase() });
      setDossier(data);
      toast.success("Rapport validé — l'expert va poster la version définitive");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setActing(false);
    }
  };

  const sendRemarks = async () => {
    if (!remarks.trim()) { toast.error("Veuillez saisir vos remarques"); return; }
    setActing(true);
    try {
      const { data } = await axios.post(`${API}/public/remarks`, { access_code: code.trim().toUpperCase(), text: remarks });
      setDossier(data);
      setShowRemarks(false);
      setRemarks("");
      toast.success("Remarques transmises à l'expert");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setActing(false);
    }
  };

  const sendMessage = async () => {
    if (!message.trim()) { toast.error("Veuillez saisir votre message"); return; }
    setActing(true);
    try {
      const { data } = await axios.post(`${API}/public/message`, { access_code: code.trim().toUpperCase(), text: message });
      setDossier(data);
      setShowMessage(false);
      setMessage("");
      toast.success("Message transmis à l'expert");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setActing(false);
    }
  };

  const paid = dossier?.payment_status === "paid";
  const rs = dossier?.report_status;
  const cv = dossier?.current_version;
  const documents = dossier?.documents || [];

  return (
    <div className="min-h-screen flex flex-col lg:flex-row" data-testid="public-portal-page">
      <div className="hidden lg:block lg:w-1/2 relative">
        <img src={HERO} alt="Architecture" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-slate-950/55" />
        <div className="relative z-10 h-full flex flex-col justify-between p-14 text-white">
          <div>
            <div className="flex items-center gap-2.5">
              <div className="w-9 h-9 rounded-md bg-blue-600 flex items-center justify-center">
                <Buildings size={20} weight="bold" />
              </div>
              <span className="font-heading text-lg font-semibold tracking-tight">Certicapitale — Isoconseil</span>
            </div>
            <p className="text-[11px] text-slate-300/80 leading-relaxed mt-2 max-w-xs">
              Certificat PEB · Réception PEB chauffage · Certibeau · Audit énergétique PAE · Réception PAC RESCERT
            </p>
          </div>
          <div>
            <h2 className="font-heading text-4xl xl:text-5xl font-medium tracking-tighter leading-[1.05] mb-4">
              Votre rapport<br />d'expertise, en<br />toute sécurité.
            </h2>
            <p className="text-slate-300 max-w-sm">
              Consultez votre rapport, transmettez vos remarques et validez la version définitive.
            </p>
          </div>
        </div>
      </div>

      <div className="flex-1 lg:w-1/2 flex items-center justify-center p-6 sm:p-12 bg-white">
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
          className="w-full max-w-md">
          {!dossier ? (
            <>
              <p className="label-eyebrow mb-3">Portail client</p>
              <h1 className="font-heading text-4xl sm:text-5xl tracking-tighter font-medium text-slate-950 mb-4">
                Accédez à votre rapport d'expertise
              </h1>
              <p className="text-slate-600 mb-8 leading-relaxed">
                Veuillez entrer le code d'accès sécurisé qui vous a été transmis.
              </p>
              <form onSubmit={submit} className="space-y-4">
                <Input data-testid="access-code-input" value={code}
                  onChange={(e) => setCode(e.target.value.toUpperCase())}
                  placeholder="Ex. A1B2C3D4"
                  className="h-14 text-lg font-mono tracking-widest text-center uppercase" maxLength={8} />
                <Button type="submit" data-testid="access-submit-button" disabled={loading}
                  className="w-full h-14 bg-blue-600 hover:bg-blue-700 text-base">
                  {loading ? "Vérification…" : "Accéder au dossier"}
                  <ArrowRight size={20} weight="bold" className="ml-1.5" />
                </Button>
              </form>
            </>
          ) : (
            <div data-testid="dossier-result">
              <button onClick={() => { setDossier(null); setCode(""); setShowRemarks(false); }} data-testid="portal-back-button"
                className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900 mb-6 transition-colors">
                <ArrowLeft size={16} /> Autre code
              </button>

              <p className="label-eyebrow mb-2">Bien expertisé</p>
              <h1 className="font-heading text-2xl sm:text-3xl tracking-tight font-medium text-slate-950 mb-1 flex items-start gap-2">
                <MapPin size={26} className="text-blue-600 shrink-0 mt-1" />
                <span>{dossier.property_address}</span>
              </h1>
              <p className="text-slate-500 mb-8 ml-8">Client : {dossier.client_name}</p>

              {/* Not paid */}
              {!paid && (
                <div className="rounded-md bg-amber-50 border border-amber-200 p-5 mb-6 flex items-start gap-3"
                  data-testid="status-pending-block">
                  <WarningCircle size={26} weight="fill" className="text-amber-600 shrink-0" />
                  <div>
                    <p className="font-medium text-amber-800">En attente de paiement</p>
                    <p className="text-sm text-amber-700">Votre rapport sera disponible dès l'enregistrement du paiement de votre facture. Vous recevrez un email de confirmation à ce moment — inutile de réessayer d'ici là.</p>
                  </div>
                </div>
              )}

              {/* Paid states */}
              {paid && (
                <>
                  {(rs === "provisional" || rs === "revision_requested" || rs === "definitive") && cv && (
                    <div className={`rounded-md border p-5 mb-4 flex items-start gap-3 ${
                      cv.kind === "definitive" ? "bg-emerald-50 border-emerald-200" : "bg-blue-50 border-blue-200"}`}
                      data-testid="report-available-block">
                      {cv.kind === "definitive"
                        ? <SealCheck size={26} weight="fill" className="text-emerald-600 shrink-0" />
                        : <CheckCircle size={26} weight="fill" className="text-blue-600 shrink-0" />}
                      <div>
                        <p className={`font-medium ${cv.kind === "definitive" ? "text-emerald-800" : "text-blue-800"}`}>
                          {cv.kind === "definitive" ? "Rapport définitif disponible" : "Rapport provisoire disponible"}
                        </p>
                        <p className={`text-sm ${cv.kind === "definitive" ? "text-emerald-700" : "text-blue-700"}`}>
                          {cv.kind === "definitive"
                            ? "Version finale de votre expertise."
                            : "Merci de relire ce document provisoire avant validation."}
                        </p>
                      </div>
                    </div>
                  )}

                  {cv && (
                    <Button data-testid="download-report-button" onClick={download}
                      className="w-full h-14 bg-blue-600 hover:bg-blue-700 text-base mb-3">
                      <DownloadSimple size={20} weight="bold" className="mr-1.5" />
                      Télécharger le rapport {cv.kind === "definitive" ? "définitif" : "provisoire"} (PDF)
                    </Button>
                  )}

                  {documents.length > 0 && (
                    <div className="space-y-2 mb-3" data-testid="documents-list">
                      <p className="label-eyebrow">Rapports disponibles ({documents.length})</p>
                      {documents.map((doc) => (
                        <button key={doc.id} data-testid={`download-doc-${doc.id}`} onClick={() => downloadDoc(doc)}
                          className="w-full flex items-center gap-2.5 p-3 rounded-md border border-slate-200 hover:border-blue-400 hover:bg-blue-50/40 transition-colors text-left">
                          <DownloadSimple size={18} className="text-blue-600 shrink-0" />
                          <span className="text-sm truncate flex-1 min-w-0">{doc.filename}</span>
                        </button>
                      ))}
                    </div>
                  )}

                  {/* Provisional -> actions */}
                  {rs === "provisional" && (
                    <div className="space-y-3 mt-4" data-testid="provisional-actions">
                      {confirmValidate && !showRemarks ? (
                        <div className="rounded-md border-2 border-emerald-300 bg-emerald-50 p-5 space-y-3" data-testid="confirm-validate-block">
                          <p className="font-medium text-emerald-900">Vous acceptez le rapport provisoire tel quel ?</p>
                          <p className="text-sm text-emerald-800">L'expert établira ensuite le rapport définitif, qui vous sera envoyé par email.</p>
                          <Button data-testid="confirm-validate-button" onClick={validate} disabled={acting}
                            className="w-full h-12 bg-emerald-600 hover:bg-emerald-700 text-white">
                            <SealCheck size={18} weight="bold" className="mr-1.5" /> Oui, j'accepte le rapport
                          </Button>
                          <Button variant="outline" data-testid="switch-to-remarks-button" onClick={() => { setConfirmValidate(false); setShowRemarks(true); }}
                            className="w-full h-11">
                            <ChatText size={18} className="mr-1.5" /> Non, j'ai des remarques
                          </Button>
                        </div>
                      ) : !showRemarks ? (
                        <>
                          <Button data-testid="validate-report-button" onClick={validate} disabled={acting}
                            className="w-full h-12 bg-emerald-600 hover:bg-emerald-700 text-white">
                            <SealCheck size={18} weight="bold" className="mr-1.5" /> Valider le rapport définitivement
                          </Button>
                          <Button variant="outline" data-testid="open-remarks-button" onClick={() => setShowRemarks(true)}
                            className="w-full h-12">
                            <ChatText size={18} className="mr-1.5" /> Envoyer des remarques
                          </Button>
                        </>
                      ) : (
                        <div className="space-y-3">
                          <Textarea data-testid="remarks-textarea" value={remarks}
                            onChange={(e) => setRemarks(e.target.value)} rows={4}
                            placeholder="Décrivez les corrections souhaitées…" className="resize-none" />
                          <div className="flex gap-2">
                            <Button data-testid="send-remarks-button" onClick={sendRemarks} disabled={acting}
                              className="flex-1 h-11 bg-blue-600 hover:bg-blue-700">Transmettre</Button>
                            <Button variant="outline" onClick={() => setShowRemarks(false)} className="h-11">Annuler</Button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Revision requested */}
                  {rs === "revision_requested" && (
                    <div className="rounded-md bg-orange-50 border border-orange-200 p-4 text-sm text-orange-800 flex items-start gap-2 mt-2"
                      data-testid="revision-block">
                      <ChatText size={20} className="text-orange-600 shrink-0" />
                      <span>Vos remarques ont été transmises. L'expert prépare une version corrigée ; vous la recevrez par email, en pièce jointe.</span>
                    </div>
                  )}

                  {/* Validated, awaiting definitive */}
                  {rs === "validated" && (
                    <div className="rounded-md bg-violet-50 border border-violet-200 p-4 text-sm text-violet-800 flex items-start gap-2"
                      data-testid="validated-block">
                      <HourglassMedium size={20} weight="fill" className="text-violet-600 shrink-0" />
                      <span>Vous avez validé le rapport. L'expert va poster la version définitive ; vous la recevrez par email, en pièce jointe.</span>
                    </div>
                  )}

                  {rs === "awaiting_report" && documents.length === 0 && (
                    <p className="text-sm text-slate-500 text-center mt-2">Votre rapport est en cours de préparation.</p>
                  )}
                </>
              )}

              {!(paid && rs === "provisional") && (
                <div className="mt-6 border-t border-slate-100 pt-5" data-testid="message-section">
                  {!showMessage ? (
                    <Button variant="outline" data-testid="open-message-button" onClick={() => setShowMessage(true)}
                      className="w-full h-12">
                      <ChatText size={18} className="mr-1.5" /> Envoyer un message / complément d'information
                    </Button>
                  ) : (
                    <div className="space-y-3">
                      <p className="text-sm text-slate-600">
                        Une information à ajouter (ex. isolation de la toiture, ventilation…) ? Écrivez-nous, l'expert la recevra.
                      </p>
                      <Textarea data-testid="message-textarea" value={message} onChange={(e) => setMessage(e.target.value)}
                        rows={4} placeholder="Votre message ou complément d'information…" className="resize-none" />
                      <div className="flex gap-2">
                        <Button data-testid="send-message-button" onClick={sendMessage} disabled={acting}
                          className="flex-1 h-11 bg-blue-600 hover:bg-blue-700">Envoyer</Button>
                        <Button variant="outline" onClick={() => setShowMessage(false)} className="h-11">Annuler</Button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
}
